//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createRefetchContainer } from "react-relay";
// calendar-profile imports
import { IContainer, IProps, IState } from "./CalendarProfile.d";
import { fragments, query } from "./CalendarProfile.gql";
import * as s from "./CalendarProfile.styles";
// other components
import { ProfileTimeVisualizations } from "@components/profile-time-visualizations";
import { ProfileWorkingPreferencesRenderer } from "@components/profile-working-preferences";
import { ProfileCollaborators } from "@components/profile-collaborators";
import { HealthPeriodPicker } from "@components/health-period-picker";
import { CalloutBox } from "@components/callout-box";
import { WeekInReviewScrubber } from "@components/week-in-review-scrubber";
import { WeekInReviewNotAvailable } from "@components/week-in-review-not-available";
import { H2 } from "@ui";
// util
import { IHealthFetchVars, getCalViewStateFromDateString } from "@util/team-health.util";
import { track, TrackingEvents, page, PageEvents } from "@util/analytics.util";
import { logger } from "@util/logger.util";
// state
import { IReduxState } from "@reducers/root.reducer";
import { snackBarOpen } from "@actions/snack-bar.actions";
import { updateOrgInvite } from "@mutations";
// material-ui
import { Button, Divider } from "@material-ui/core";
import { Mail } from "@material-ui/icons";
import withStyles from "@material-ui/core/styles/withStyles";

//////////////////
// COMPONENT
//////////////////
class CalendarProfileCmpt extends React.Component<IProps, IState> {
  // ~-~-~-~-~-~-~-
  // Setup
  // ~-~-~-~-~-~-~-
  constructor(props: IProps) {
    super(props);
    this.state = {
      loading: false,
      rewindWeek: this.props.rewindWeek,
      inviteSending: false,
      inviteSent: false,
    };
  }

  public componentDidMount() {
    page(PageEvents.TEAM_HEALTH_PROFILE);
  }

  // ~-~-~-~-~-~-~-
  // Async
  // ~-~-~-~-~-~-~-
  private refetchCallback = (error?: Error) => {
    if (error) {
      logger.error("Error on CalendarProfile refetch", error);
    }
    this.setState({ loading: false });
  };

  private refetch = (vars: IHealthFetchVars) => {
    this.setState({ loading: true, rewindWeek: vars.rangeMin.date || this.props.rewindWeek });
    this.props.relay.refetch(
      { ...vars, week: vars.rangeMin.date },
      undefined,
      this.refetchCallback,
    );
    track(TrackingEvents.TEAM_HEALTH.SELECT_TIME_RANGE, { ...vars });
  };

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private onInviteSuccess = () => {
    this.setState({ inviteSending: false, inviteSent: true });
    this.props.snackBarOpen("Invite sent!");
  };

  private onInviteFailure = () => {
    this.setState({ inviteSending: false });
    logger.error("Failed to send invite from Calendar Profile");
    this.props.snackBarOpen("Failed to send invite.", "error");
  };

  private sendInvite = (_e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    this.setState({ inviteSending: true });
    updateOrgInvite(
      this.props.relay.environment,
      { calendarIds: [this.props.calendarId], orgRelayId: this.props.org.id },
      this.onInviteSuccess,
      this.onInviteFailure,
    );

    // callback
    track(TrackingEvents.TEAM_HEALTH.EXPERIMENT_CALENDAR_PROFILE_GATED_TEAM_HEALTH_INVITE_CLICKED, {
      calendarId: this.props.calendarId,
    });
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private maybeGateTeamHealth = (person: ISchema.IPerson | null) => {
    const gateTeamHealth = !!person && !person.isUser;

    const displayName =
      !!person && person.profile && person.profile.givenName && person.profile.familyName
        ? `${person.profile.givenName} ${person.profile.familyName}`
        : this.props.calendarId;

    if (gateTeamHealth) {
      return (
        <div className={this.props.classes.gatedContainer}>
          <div style={s.styles.gatedMessage}>
            {displayName} is not yet using Clockwise, invite {displayName} to view their analytics
          </div>
          <Button
            variant="contained"
            color="primary"
            classes={{ root: this.props.classes.inviteButton }}
            disabled={this.state.inviteSending || this.state.inviteSent}
            onClick={this.sendInvite}
            fullWidth
          >
            <Mail {...s.buttonIcon} />
            <div>
              {this.state.inviteSending
                ? "Sending..."
                : this.state.inviteSent
                ? "Invite Sent"
                : "Invite to Clockwise"}
            </div>
          </Button>
        </div>
      );
    }

    return <span />;
  };

  public render() {
    const { org, stale, initialFetchError, showWeekInReview, calendarId } = this.props;
    const { loading, refetchError } = this.state;

    // catch errors in fetches
    if (!!initialFetchError || !!refetchError) {
      return (
        <CalloutBox info style={s.styles.warning}>
          Clockwise does not have enough data for this teammate to show their profile.
        </CalloutBox>
      );
    }

    const profileViewWeek = this.props.viewWeek
      ? getCalViewStateFromDateString(this.props.viewWeek)
      : undefined;
    let person: ISchema.IPerson | null = null;
    let userId = "";
    if (
      org.personsByCalendarIdsErrorable.__typename === "PersonList" &&
      org.personsByCalendarIdsErrorable.list.length
    ) {
      person = org.personsByCalendarIdsErrorable.list[0];
      userId = person.userId;
    }

    const rewindHistory =
      !!org.rewindHistoryErrorable && org.rewindHistoryErrorable.__typename === "RewindHistory"
        ? org.rewindHistoryErrorable
        : null;
    const rewindHistoryStatusCode =
      !!org.rewindHistoryErrorable && org.rewindHistoryErrorable.__typename === "GraphEntityError"
        ? org.rewindHistoryErrorable.statusCode
        : null;

    const gateTeamHealth = !!person && !person.isUser;

    return (
      <div
        style={{ ...s.styles.relativeWrapper, ...(gateTeamHealth ? s.styles.hideOverflow : {}) }}
      >
        {this.maybeGateTeamHealth(person)}
        <div
          className={`${this.props.classes.container} ${
            gateTeamHealth ? this.props.classes.blur : ""
          }`}
        >
          <div className={this.props.classes.pickerContainer}>
            <HealthPeriodPicker
              orgId={org.id}
              disabled={loading || stale}
              onChange={(vars) => this.refetch(vars)}
              overrideCalViewState={profileViewWeek}
            />
          </div>
          {!!showWeekInReview ? (
            <div>
              <H2 style={s.styles.title}>Week in review</H2>
              {!!rewindHistory ? (
                <WeekInReviewScrubber
                  rewindHistory={rewindHistory}
                  rewindWeek={this.state.rewindWeek}
                />
              ) : (
                <WeekInReviewNotAvailable
                  org={org}
                  person={person}
                  statusCode={rewindHistoryStatusCode}
                  environment={this.props.relay.environment}
                />
              )}
              <Divider style={s.styles.divider} />
            </div>
          ) : (
            ""
          )}
          <ProfileTimeVisualizations org={org} />
          <ProfileWorkingPreferencesRenderer
            org={org}
            userId={userId ? userId : undefined}
            nonUserCalendarId={calendarId}
            environment={this.props.relay.environment}
          />
          <ProfileCollaborators org={org} orgRefetch={org} parentLoading={this.state.loading} />
        </div>
      </div>
    );
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    refetchFlag: state.teamHealth.refetchTeamHealth,
    viewWeek: state.webExtension.activeViewCalendarProfile.viewWeek,
  };
}

const CalendarProfileRedux = connect(mapStateToProps, {
  snackBarOpen,
})(CalendarProfileCmpt);

//////////////////
// WITH STYLES
//////////////////
const CalendarProfileStyled = withStyles(s.styles)(CalendarProfileRedux);

//////////////////
// RELAY
//////////////////
export const CalendarProfile = createRefetchContainer<IContainer>(
  CalendarProfileStyled,
  fragments,
  query,
);
